import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Avatar,
  useTheme,
  useMediaQuery,
} from '@mui/material';

import { CircleUser, LogOut, User, Menu as MenuIcon } from 'lucide-react';
import Dialog from '../Dialog';
import Button from '../Button';

/**
 * Header for admin/user layouts
 */

const InnerHeader = ({
  user,
  profileLink,
  onLogout,
  title,
  showProfileUI = false,
  onToggleSidebar
}) => {
  const location = useLocation();


  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  return (
    <AppBar position="sticky" elevation={0}>
      <Toolbar>

        {/* Hamburger for mobile */}
        {isMobile && (
          <IconButton
            edge="start"
            onClick={onToggleSidebar}
            sx={{ mr: 1 }}
          >
            <MenuIcon size={22} />
          </IconButton>
        )}

        {/* Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
          {title ? (
            <>
              <Box
                sx={{
                  width: 4,
                  height: 28,
                  borderRadius: 2,
                  bgcolor: 'primary.main'
                }}
              />
              <Typography variant="h6" color="inherit">
                {title}
              </Typography>
            </>
          ) : (
            <Box />
          )}
        </Box>

        {/* Profile Menu */}
        {showProfileUI && user && (
          <>
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              aria-label="Profile menu"
              aria-controls={open ? 'profile-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              sx={{ p: 0.5, border: '1px solid', borderColor: 'divider' }}
            >
              <Avatar
                src={user?.profileImage}
                alt={user?.name || 'User'}
                sx={{ width: 32, height: 32, fontSize: '1rem', fontWeight: 600 }}
              >
                {!user?.profileImage && (user?.name?.[0] || <CircleUser size={20} />)}
              </Avatar>
            </IconButton>

            <Menu
              id="profile-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >

              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: 1,
                  borderColor: 'grey.100',
                  bgcolor: 'grey.50'
                }}
              >
                <Typography variant="subtitle2" noWrap>
                  {user?.name || 'User'}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  display="block"
                >
                  {user?.email}
                </Typography>
              </Box>

              {profileLink && user?.role !== 'admin' && (
                <MenuItem
                  component={Link}
                  to={profileLink}
                  onClick={handleMenuClose}
                  selected={location.pathname === profileLink}
                >
                  <ListItemIcon>
                    <User size={20} strokeWidth={1.75} />
                  </ListItemIcon>
                  <ListItemText primary="Profile" />
                </MenuItem>
              )}

              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  setLogoutModalOpen(true);
                }}
                sx={{ color: 'error.main' }}
              >
                <ListItemIcon>
                  <LogOut size={20} strokeWidth={1.75} />
                </ListItemIcon>
                <ListItemText primary="Logout" />
              </MenuItem>

            </Menu>
          </>
        )}

      </Toolbar>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutModalOpen}
        onClose={() => setLogoutModalOpen(false)}
        title="Confirm Logout"
        size="md"
        footer={
          <div className="flex justify-end gap-3 w-full">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setLogoutModalOpen(false)}
            >
              No
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                setLogoutModalOpen(false);
                onLogout?.();
              }}
            >
              Yes
            </Button>
          </div>
        }
      >
        <p className="text-gray-600">Are you sure you want to log out of your account?</p>
      </Dialog>
    </AppBar>
  );
};

export default InnerHeader;